import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper
} from '@mui/material';
import formatCurrency from '../../constants/formateCurrency';

const PriceSummary = ({
    basePrice = 0,
    discountAmount = 0,
    completionFee = 0,
    customPrice = null,
    makeHugeSale = false,
    selectedBranch,
    branches = [],
    selectedScans = [],
    scansList = [],
    t
}) => {
    // Debug: Log incoming props to diagnose issues
    console.log('PriceSummary Props:', {
        basePrice,
        discountAmount,
        completionFee,
        customPrice,
        makeHugeSale
    });

    const branch = branches?.find(b => b._id === selectedBranch);

    // Ensure numeric conversion
    const numBasePrice = Number(basePrice) || 0;
    const numCompletionFee = Number(completionFee) || 0;
    const numDiscountAmount = Number(discountAmount) || 0;
    const numCustomPrice = Number(customPrice) || 0;

    // Calculate Final Price - FIXED CALCULATION
    let preDiscountTotal = 0;

    if (makeHugeSale && customPrice != null && numCustomPrice > 0) {
        preDiscountTotal = numCustomPrice;
    } else {
        preDiscountTotal = numBasePrice + numCompletionFee;
    }

    // Apply discount to get the actual final price
    const calculatedFinalPrice = preDiscountTotal - numDiscountAmount;
    const finalPrice = Math.max(0, calculatedFinalPrice);

    // Get scan names with quantities
    const scansDisplay = selectedScans
        ?.filter(scan => scan.scan)
        ?.map(scan => {
            // Handle both object and string id
            const scanId = typeof scan.scan === 'object' ? scan.scan._id : scan.scan;
            const scanName = typeof scan.scan === 'object' ? scan.scan.name : null;

            const scanDetails = scanName
                ? { name: scanName }
                : scansList?.find(s => s._id === scanId);

            if (!scanDetails) return null;
            return scan.quantity > 1
                ? `${scanDetails.name} (×${scan.quantity})`
                : scanDetails.name;
        })
        .filter(Boolean)
        .join(', ');

    return (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
                <TableBody>
                    {/* Branch Row */}
                    {branch && (
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('appointments.branch')}
                            </TableCell>
                            <TableCell align="right">{branch.name}</TableCell>
                        </TableRow>
                    )}

                    {/* Scans Row */}
                    {scansDisplay && (
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('navigation.scans')}
                            </TableCell>
                            <TableCell align="right">{scansDisplay}</TableCell>
                        </TableRow>
                    )}

                    {/* Custom Price or Base Price */}
                    {makeHugeSale && numCustomPrice > 0 ? (
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('appointments.customPrice')}
                            </TableCell>
                            <TableCell align="right">
                                {formatCurrency(numCustomPrice)}
                            </TableCell>
                        </TableRow>
                    ) : (
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('appointments.basePrice')}
                            </TableCell>
                            <TableCell align="right">
                                {formatCurrency(numBasePrice)}
                            </TableCell>
                        </TableRow>
                    )}

                    {/* Completion Fee */}
                    {!makeHugeSale && numCompletionFee > 0 && (
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('appointments.completionFee')}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                                +{formatCurrency(numCompletionFee)}
                            </TableCell>
                        </TableRow>
                    )}

                    {/* Subtotal (before discount) - Optional display */}
                    {numDiscountAmount > 0 && (
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('appointments.subtotal')}
                            </TableCell>
                            <TableCell align="right">
                                {formatCurrency(preDiscountTotal)}
                            </TableCell>
                        </TableRow>
                    )}

                    {/* Discount */}
                    {numDiscountAmount > 0 && (
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('appointments.discount')}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>
                                -{formatCurrency(numDiscountAmount)}
                            </TableCell>
                        </TableRow>
                    )}

                    {/* Final Price */}
                    <TableRow
                        sx={{
                            backgroundColor: 'primary.main',
                            '& > *': { color: 'white !important' }
                        }}
                    >
                        <TableCell sx={{ fontWeight: 'bold' }}>
                            {t('appointments.finalPrice')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(finalPrice)}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default PriceSummary;